import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export interface Language {
  code: string;
  name: string;
  nativeName: string;
}

export interface PowerPointSettings {
  fontFamily: string;
  fontSizeTitle: number;
  fontSizeHeading: number;
  fontSizeBody: number;
  titleColor: string;
  headingColor: string;
  bodyColor: string;
  coverBodyColor: string;
  backgroundCover?: string;
  backgroundSplit?: string;
  backgroundText?: string;
}

export interface OrganisationSettings {
  id: number;
  organizationId: number;
  validationUpper: number;
  validationMiddle: number;
  validationLower: number;
  logoUrl?: string;
  defaultLanguage: string;
  aiModel: string;
  powerPointSettings?: PowerPointSettings;
  // Whitelabel settings
  whitelabelProductName?: string;
  whitelabelPrimaryLogoUrl?: string;
  whitelabelSecondaryLogoUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateOrganisationSettingsRequest {
  organizationId: number;
  validationUpper: number;
  validationMiddle: number;
  validationLower: number;
  logoUrl?: string;
  defaultLanguage?: string;
  aiModel?: string;
}

export interface UpdateOrganisationSettingsRequest {
  validationUpper?: number;
  validationMiddle?: number;
  validationLower?: number;
  logoUrl?: string | null;
  defaultLanguage?: string;
  aiModel?: string;
  powerPointSettings?: Partial<PowerPointSettings>;
}

class OrganisationSettingsService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  // Get current user's organization settings
  async getOrganisationSettings(): Promise<OrganisationSettings> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/organisation-settings`,
        { headers: this.getAuthHeaders() }
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch organisation settings');
      }
    } catch (error: any) {
      console.error('Error fetching organisation settings:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch organisation settings');
    }
  }

  // Get organisation settings by organization ID (admin only)
  async getOrganisationSettingsByOrgId(organizationId: number): Promise<OrganisationSettings> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/organisation-settings/${organizationId}`,
        { headers: this.getAuthHeaders() }
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch organisation settings');
      }
    } catch (error: any) {
      console.error('Error fetching organisation settings by ID:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch organisation settings');
    }
  }

  // Get all organisation settings (super admin only)
  async getAllOrganisationSettings(): Promise<OrganisationSettings[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/organisation-settings/all`,
        { headers: this.getAuthHeaders() }
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch all organisation settings');
      }
    } catch (error: any) {
      console.error('Error fetching all organisation settings:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch all organisation settings');
    }
  }

  // Update current user's organization settings
  async updateOrganisationSettings(updates: UpdateOrganisationSettingsRequest): Promise<OrganisationSettings> {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/organisation-settings`,
        updates,
        { headers: this.getAuthHeaders() }
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update organisation settings');
      }
    } catch (error: any) {
      console.error('Error updating organisation settings:', error);
      throw new Error(error.response?.data?.message || 'Failed to update organisation settings');
    }
  }

  // Update organisation settings by organization ID (admin only)
  async updateOrganisationSettingsByOrgId(
    organizationId: number, 
    updates: UpdateOrganisationSettingsRequest
  ): Promise<OrganisationSettings> {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/organisation-settings/${organizationId}`,
        updates,
        { headers: this.getAuthHeaders() }
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update organisation settings');
      }
    } catch (error: any) {
      console.error('Error updating organisation settings by ID:', error);
      throw new Error(error.response?.data?.message || 'Failed to update organisation settings');
    }
  }

  // Create organisation settings (super admin only)
  async createOrganisationSettings(settings: CreateOrganisationSettingsRequest): Promise<OrganisationSettings> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/organisation-settings`,
        settings,
        { headers: this.getAuthHeaders() }
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to create organisation settings');
      }
    } catch (error: any) {
      console.error('Error creating organisation settings:', error);
      throw new Error(error.response?.data?.message || 'Failed to create organisation settings');
    }
  }

  // Delete organisation settings (super admin only)
  async deleteOrganisationSettings(organizationId: number): Promise<void> {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/organisation-settings/${organizationId}`,
        { headers: this.getAuthHeaders() }
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete organisation settings');
      }
    } catch (error: any) {
      console.error('Error deleting organisation settings:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete organisation settings');
    }
  }

  // Upload organisation logo
  async uploadLogo(file: File): Promise<{ logoUrl: string }> {
    try {
      const formData = new FormData();
      formData.append('logo', file);

      console.log('Uploading to:', `${API_BASE_URL}/organisation-settings/upload-logo`);
      console.log('File details:', { name: file.name, size: file.size, type: file.type });

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/organisation-settings/upload-logo`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      console.log('Upload response:', response.data);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to upload logo');
      }
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);
      throw new Error(error.response?.data?.message || error.message || 'Failed to upload logo');
    }
  }

  async getSupportedLanguages(): Promise<Language[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/organisation-settings/languages`, {
        headers: this.getAuthHeaders(),
      });
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching supported languages:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch supported languages');
    }
  }

  // Upload PowerPoint background image
  async uploadPPTBackground(type: 'cover' | 'split' | 'text', file: File): Promise<{ backgroundUrl: string; type: string }> {
    try {
      const formData = new FormData();
      formData.append('background', file);

      console.log(`Uploading PPT ${type} background:`, file.name);

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/organisation-settings/upload-ppt-background/${type}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      console.log('Upload response:', response.data);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to upload background');
      }
    } catch (error: any) {
      console.error('Error uploading PPT background:', error);
      throw new Error(error.response?.data?.message || 'Failed to upload background');
    }
  }

  // ============================================================================
  // WHITELABEL METHODS
  // ============================================================================

  // Upload whitelabel logo (primary or secondary)
  async uploadWhitelabelLogo(type: 'primary' | 'secondary', file: File): Promise<{ logoUrl: string; type: string }> {
    try {
      const formData = new FormData();
      formData.append('logo', file);

      console.log(`Uploading whitelabel ${type} logo:`, file.name);

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/organisation-settings/whitelabel-logo/${type}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      console.log('Upload response:', response.data);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to upload logo');
      }
    } catch (error: any) {
      console.error('Error uploading whitelabel logo:', error);
      throw new Error(error.response?.data?.message || 'Failed to upload logo');
    }
  }

  // Delete whitelabel logo
  async deleteWhitelabelLogo(type: 'primary' | 'secondary'): Promise<void> {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/organisation-settings/whitelabel-logo/${type}`,
        { headers: this.getAuthHeaders() }
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete logo');
      }
    } catch (error: any) {
      console.error('Error deleting whitelabel logo:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete logo');
    }
  }

  // Update whitelabel product name
  async updateWhitelabelProductName(productName: string | null): Promise<{ productName: string | null }> {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/organisation-settings/whitelabel-product-name`,
        { productName },
        { headers: this.getAuthHeaders() }
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update product name');
      }
    } catch (error: any) {
      console.error('Error updating whitelabel product name:', error);
      throw new Error(error.response?.data?.message || 'Failed to update product name');
    }
  }
}

export default new OrganisationSettingsService();
